import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ArrowLeft, User, Shield, ShieldOff, Download } from 'lucide-react';

const SUPER_ADMIN_EMAIL = 'asili@asili.immo';


export default function AdminUsers() {
 const navigate = useNavigate();
 const queryClient = useQueryClient();
 const [user, setUser] = useState(null);
 const [searchTerm, setSearchTerm] = useState('');
 const [pendingId, setPendingId] = useState(null);
 const [feedback, setFeedback] = useState(null);

 const isSuperAdmin = user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL;

 // Auto-scroll to bottom on mobile
 useEffect(() => {
   if (window.innerWidth < 768) {
     setTimeout(() => {
       window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
     }, 100);
   }
 }, []);

 useEffect(() => {
   supabase.auth.getUser().then(async ({ data: { user: authUser } }) => {
       if (!authUser) { navigate('/map'); return; }
       const { data: profile } = await supabase.from('users').select('*').eq('id', authUser.id).single();
       if (!profile || profile.role !== 'admin') {
         navigate('/map');
       } else {
         setUser({ ...authUser, ...profile });
       }
     }).catch(() => navigate('/map'));
 }, [navigate]);


 const { data: users = [] } = useQuery({
   queryKey: ['adminUsers'],
   queryFn: async () => {
     const allUsers = await base44.entities.User.list('-created_date', 500);
     return allUsers;
   },
   enabled: !!user?.id
 });


 const { data: userStats = {} } = useQuery({
   queryKey: ['adminUserStats'],
   queryFn: async () => {
     const xpEvents = await base44.entities.XPEvents.list('-created_at', 5000);
     const stats = {};
    
     xpEvents.forEach(event => {
       if (!stats[event.user_id]) {
         stats[event.user_id] = { total_xp: 0 };
       }
       stats[event.user_id].total_xp += event.xp_amount;
     });


     return stats;
   }
 });


 const setRoleMutation = useMutation({
   mutationFn: async ({ userId, role }) => {
     const { data: { session } } = await supabase.auth.getSession();
     if (!session?.access_token) throw new Error('Session expirée, reconnecte-toi');
     const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/setUserRole`, {
       method: 'POST',
       headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
       body: JSON.stringify({ user_id: userId, role }),
     });
     const json = await response.json();
     if (!response.ok) throw new Error(json.error || 'Erreur serveur');
     return json.user;
   },
   onMutate: ({ userId }) => setPendingId(userId),
   onSuccess: (updated) => {
     setFeedback({ type: 'ok', msg: `Rôle mis à jour : ${updated.email} → ${updated.role}` });
     queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
   },
   onError: (err) => setFeedback({ type: 'err', msg: err.message }),
   onSettled: () => {
     setPendingId(null);
     setTimeout(() => setFeedback(null), 4000);
   },
 });

 const handleToggleRole = (target) => {
   const nextRole = target.role === 'admin' ? 'user' : 'admin';
   const action = nextRole === 'admin' ? 'Promouvoir' : 'Retirer';
   if (!confirm(`${action} ${target.email || target.full_name} ${nextRole === 'admin' ? 'en admin' : 'du rôle admin'} ?`)) return;
   setRoleMutation.mutate({ userId: target.id, role: nextRole });
 };

 const exportCSV = () => {
   const headers = ['ID', 'Nom', 'Email', 'Username', 'Niveau', 'XP Total', 'XP Semaine', 'XP Jour', 'Rôle', 'Inscrit le'];
   const rows = users.map(u => [
     u.id,
     u.full_name || '',
     u.email || '',
     u.username || '',
     u.level || 1,
     u.xp_total || userStats[u.id]?.total_xp || 0,
     u.xp_week || 0,
     u.xp_day || 0,
     u.role || 'user',
     new Date(u.created_date).toLocaleDateString('fr-FR')
   ]);

   const csvContent = [headers, ...rows]
     .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
     .join('\n');

   const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
   const url = URL.createObjectURL(blob);
   const a = document.createElement('a');
   a.href = url;
   a.download = `utilisateurs_${new Date().toISOString().slice(0, 10)}.csv`;
   a.click();
   URL.revokeObjectURL(url);
 };

 const filteredUsers = users.filter(u =>
   u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
   u.email?.toLowerCase().includes(searchTerm.toLowerCase())
 );


 if (!user) {
   return <div className="flex items-center justify-center min-h-screen text-white">Chargement...</div>;
 }


 return (
   <div className="bg-[#0A0B10] text-white p-6">
     <div className="max-w-6xl mx-auto">
       {/* Header */}
       <div className="flex items-center gap-4 mb-6">
         <button
           className="text-[#B3B6C2] hover:text-white"
           onClick={() => navigate('/admin')}
         >
           <ArrowLeft className="w-5 h-5" />
         </button>
         <h1 className="text-lg font-black uppercase tracking-tight">Utilisateurs</h1>
         <button
           onClick={exportCSV}
           className="ml-auto flex items-center gap-2 bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
         >
           <Download className="w-4 h-4" />
           Export CSV ({users.length})
         </button>
         </div>


       {/* Search */}
       <div className="mb-6">
         <Input
           placeholder="Rechercher un utilisateur..."
           value={searchTerm}
           onChange={(e) => setSearchTerm(e.target.value)}
           className="bg-[#13151D] border-[#6E2BFF] text-white placeholder-[#B3B6C2]"
         />
       </div>

       {feedback && (
         <div className={`mb-4 p-3 rounded-lg text-sm font-semibold ${
           feedback.type === 'ok'
             ? 'bg-green-900/40 border border-green-600 text-green-200'
             : 'bg-red-900/40 border border-red-600 text-red-200'
         }`}>
           {feedback.msg}
         </div>
       )}


       {/* Users List */}
       <div className="grid gap-4">
         {filteredUsers.length === 0 ? (
           <p className="text-[#B3B6C2] text-center py-8">Aucun utilisateur trouvé</p>
         ) : (
           filteredUsers.map(u => (
             <Card
               key={u.id}
               className="bg-[#13151D] border-l-4 border-l-[#6E2BFF] p-4 hover:bg-[#1B1E28] transition-all"
             >
               <div className="flex items-start justify-between gap-4">
                 {isSuperAdmin && u.email?.toLowerCase() !== SUPER_ADMIN_EMAIL && (
                   <button
                     onClick={() => handleToggleRole(u)}
                     disabled={pendingId === u.id}
                     className={`order-2 shrink-0 flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                       u.role === 'admin'
                         ? 'bg-red-600 hover:bg-red-700 text-white'
                         : 'bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white'
                     }`}
                     title={u.role === 'admin' ? 'Retirer le rôle admin' : 'Promouvoir admin'}
                   >
                     {u.role === 'admin' ? <ShieldOff className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                     {pendingId === u.id ? '...' : (u.role === 'admin' ? 'Retirer admin' : 'Promouvoir')}
                   </button>
                 )}
                 <div className="flex-1 text-white order-1">
                   <div className="flex items-center gap-2 mb-2">
                     <User className="w-4 h-4 text-[#6E2BFF]" />
                     <h3 className="text-lg font-bold">{u.full_name}</h3>
                     {u.role === 'admin' && (
                       <span className="flex items-center gap-1 text-xs text-[#6E2BFF]">
                         <Shield className="w-3 h-3" /> Admin
                       </span>
                     )}
                   </div>
                   <p className="text-sm text-[#B3B6C2] mb-1">{u.email}</p>
                   <p className="text-xs text-[#6E2BFF] font-mono mb-3">ID: {u.id}</p>
                  
                   <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-[#1B1E28]">
                     <div>
                       <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Inscrit le</p>
                       <p className="text-white text-sm mt-1">{new Date(u.created_date).toLocaleDateString('fr-FR')}</p>
                     </div>
                     <div>
                       <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Total XP</p>
                       <p className="text-white font-bold mt-1">{userStats[u.id]?.total_xp || 0}</p>
                     </div>
                     <div>
                       <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Niveau</p>
                       <p className="text-white font-bold mt-1">{u.level || 1}</p>
                     </div>
                     <div>
                       <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Statut</p>
                       <p className="text-white font-bold text-xs mt-1">Actif</p>
                     </div>
                   </div>
                 </div>
               </div>
             </Card>
           ))
         )}
       </div>
     </div>
   </div>
 );
}